import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  showConfirmToast,
  showError,
  showLoading,
  showSuccess,
} from '../../components/Ui/ToastNotification';
import toast from 'react-hot-toast';
import api from '../../services/Api';
import { countryNameToAbbreviation } from '../../data/countries';
import { fetchUsers } from '../../services/UserManagement/UserService';

const TOAST_IDS = {
  FETCH_LOADING: 'fetch-loading',
  FETCH_SUCCESS: 'fetch-success',
  FETCH_ERROR: 'fetch-error',
  DELETE_LOADING: 'delete-loading',
  DELETE_SUCCESS: 'delete-success',
  DELETE_ERROR: 'delete-error',
};

const useUserManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [activePopoverId, setActivePopoverId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const { currentPage, entriesPerPage } = useSelector(state => state.pagination);
  const { status, isVerified } = useSelector(state => state.filter);
  const searchQuery = useSelector(state => state.search.query);

  useEffect(() => {
    let isMounted = true;
    let loadingToastId;

    const fetchData = async () => {
      if (isLoading) return;

      setIsLoading(true);
      toast.dismiss(TOAST_IDS.FETCH_LOADING);

      try {
        const formattedPeople = await fetchUsers();

        if (isMounted) {
          setAllUsers(formattedPeople);
          toast.dismiss(loadingToastId);
          showSuccess('User data loaded successfully!', { id: TOAST_IDS.FETCH_SUCCESS });
        }
      } catch (error) {
        if (isMounted) {
          toast.dismiss(loadingToastId);
          showError(
            error.response?.data?.message || 'Failed to fetch user data. Please try again.',
            { id: TOAST_IDS.FETCH_ERROR }
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(fetchData, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      toast.dismiss(loadingToastId);
    };
  }, [status, isVerified]);

  const getCountryAbbreviation = useCallback(countryName => {
    if (!countryName) return 'UN';

    if (countryNameToAbbreviation[countryName]) {
      return countryNameToAbbreviation[countryName];
    }

    const nameWithUnderscores = countryName.replace(/\s+/g, '_');
    if (countryNameToAbbreviation[nameWithUnderscores]) {
      return countryNameToAbbreviation[nameWithUnderscores];
    }

    console.log(`No match found for country: ${countryName}`);
    return 'UN';
  }, []);

  const filterUsers = useCallback((users, query, statusFilter, verificationFilter) => {
    const lowerQuery = query?.toLowerCase() || '';

    return users.filter(user => {
      const matchesSearch =
        !query ||
        [user.firstName, user.lastName, user.email, user.phoneNumber].some(field =>
          field?.toLowerCase().includes(lowerQuery)
        );

      // Status filter
      const matchesStatus =
        !statusFilter || (statusFilter === 'active' ? user.isActive : !user.isActive);

      // Verification filter
      const matchesVerification =
        verificationFilter === null || user.isVerified === (verificationFilter === 'true');

      return matchesSearch && matchesStatus && matchesVerification;
    });
  }, []);

  const filteredPeople = useMemo(() => {
    return filterUsers(allUsers, searchQuery, status, isVerified);
  }, [allUsers, searchQuery, status, isVerified, filterUsers]);

  const paginatedPeople = useMemo(() => {
    return filteredPeople.slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage);
  }, [filteredPeople, currentPage, entriesPerPage]);

  const handleDeleteUser = useCallback(async id => {
    toast.dismiss(TOAST_IDS.DELETE_LOADING);
    toast.dismiss(TOAST_IDS.DELETE_SUCCESS);
    toast.dismiss(TOAST_IDS.DELETE_ERROR);

    const loadingToastId = showLoading('Deleting user...', { id: TOAST_IDS.DELETE_LOADING });

    try {
      await api.delete(`/admin/user/user/${id}`);
      setAllUsers(prev => prev.filter(user => user._id !== id));
      toast.dismiss(loadingToastId);
      showSuccess('User has been deleted.', { id: TOAST_IDS.DELETE_SUCCESS });
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.dismiss(loadingToastId);

      if (
        error.response?.data?.statusCode === 404 &&
        error.response?.data?.message?.includes('Business with ID')
      ) {
        showError('Cannot delete user with missing business references.', {
          id: TOAST_IDS.DELETE_ERROR,
        });
      } else {
        showError('Failed to delete user.', { id: TOAST_IDS.DELETE_ERROR });
      }
    }
  }, []);

  const deleteUser = useCallback(
    id => {
      showConfirmToast({
        message: 'Are you sure you want to delete this user?',
        toastId: `confirm-delete-${id}`,
        onConfirm: () => handleDeleteUser(id),
        onCancel: () => toast.dismiss(`confirm-delete-${id}`),
      });
    },
    [handleDeleteUser]
  );

  const exportToCSV = useCallback(() => {
    const headers = ['ID', 'Title', 'Name', 'Phone', 'Email', 'No of Business', 'Status'];
    const data = filteredPeople.map(user => [
      user._id,
      user.role,
      user.name,
      user.phoneNumber,
      user.email,
      user.business_count,
      user.isActive ? 'Active' : 'Inactive',
    ]);

    const csvContent = [headers.join(','), ...data.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'user_data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredPeople]);

  const handlePrint = useCallback(() => {
    const printContent = document.getElementById('user-table').innerHTML;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = `<table style="width: 100%; border-collapse: collapse;">${printContent}</table>`;
    window.print();
    document.body.innerHTML = originalContent;
  }, []);

  const togglePopover = useCallback(id => {
    setActivePopoverId(prev => (prev === id ? null : id));
  }, []);

  return {
    activePopoverId,
    people: allUsers,
    filteredPeople,
    paginatedPeople,
    totalEntries: filteredPeople.length,
    currentPage,
    entriesPerPage,
    deleteUser,
    exportToCSV,
    handlePrint,
    togglePopover,
    getCountryAbbreviation,
    status,
    isVerified,
    isLoading,
  };
};

export default useUserManagement;
